import { DeviceDetailsContext } from "@/Components/BifrostPageComponent/DeviceComponents/DeviceDetailsComponent/DeviceDetailsComponent"
import { useCIDR, useEntriesV2 } from "@/Hooks"
import { DeviceContext } from "@/Pages/MainPanel/MainPanelBody/DevicesPage/DeviceDetailsPage/DeviceDetailsPage"
import { useContext, useEffect, useReducer } from "react"
import { useParams } from "react-router-dom"

const initialState = {
	open: false,
	isDisabled: false,
	isFetching: false,
	isSuccess: false,
	isError: false,
	errorMessage: "",
	isInvalidInput: {
		address: false,
		netmask: false,
		gateway: false,
		dns: false
	},
	isDhcpEnabled: false,
	originEthernetConfigure: {
		id: null,
		netConfiguration: {
			dhcp: null,
			address: null,
			netmask: null,
			gateway: null,
			dns: null
		}
	},
	requiredEthernetConfigureChange: {
		id: null,
		net_configuration: {
			dhcp: null,
			address: null,
			netmask: null,
			gateway: "0.0.0.0",
			dns: "0.0.0.0"
		}
	}
}

const reducer = (state, action) => {
	switch (action.type) {
		case "TOGGLE_MODAL":
			return { ...state, open: action.payload !== undefined ? action.payload : !state.open }
		case "SET_DISABLED":
			return { ...state, isDisabled: action.payload }
		case "SET_INVALID_INPUT":
			return { ...state, isInvalidInput: { ...state.isInvalidInput, ...action.payload } }
		case "TOGGLE_DHCP":
			return {
				...state,
				isDhcpEnabled: action.payload,
				requiredEthernetConfigureChange: {
					...state.requiredEthernetConfigureChange,
					net_configuration: {
						...state.requiredEthernetConfigureChange.net_configuration,
						dhcp: action.payload
					}
				}
			}
		case "UPDATE_ORIGIN_ETHERNET_CONFIGURE":
			return {
				...state,
				originEthernetConfigure: {
					...state.originEthernetConfigure,
					netConfiguration: {
						...state.originEthernetConfigure.netConfiguration,
						...action.payload
					}
				}
			}
		case "UPDATE_UNIT_CONFIGURE":
			return {
				...state,
				requiredEthernetConfigureChange: {
					...state.requiredEthernetConfigureChange,
					net_configuration: {
						...state.requiredEthernetConfigureChange.net_configuration,
						...action.payload
					}
				}
			}
		case "SET_DEVICE_ID":
			return {
				...state,
				requiredEthernetConfigureChange: {
					...state.requiredEthernetConfigureChange,
					id: action.payload
				}
			}
		case "IS_ERROR":
			return { ...state, isError: true, errorMessage: action.payload, isFetching: false, isSuccess: false }
		case "IS_SUCCESS":
			return { ...state, isSuccess: true, isFetching: false, isError: false, errorMessage: "" }
		case "IS_FETCHING":
			return { ...state, isFetching: action.payload, isSuccess: false, isError: false, errorMessage: "" }
		case "RESET_STATES":
			return {
				...initialState,
				isDhcpEnabled: action.payload,
				originEthernetConfigure: state.originEthernetConfigure,
				requiredEthernetConfigureChange: {
					id: state.requiredEthernetConfigureChange.id,
					net_configuration: { ...state.originEthernetConfigure.netConfiguration }
				}
			}
		default:
			return state
	}
}

let refreshTimeout = null

export const useConfigureEthernetSettings = () => {
	const { deviceDetails } = useContext(DeviceContext)
	const { unitConfig } = useContext(DeviceDetailsContext)
	const { validateAddress } = useCIDR()
	const { data } = unitConfig.unitConfigData
	const { id: deviceId } = useParams()
	const [state, dispatch] = useReducer(reducer, initialState)
	const [postState, { postData }] = useEntriesV2(
		"/api/v2/device/configure/ethernet",
		() => {
			dispatch({ type: "IS_SUCCESS" })
			if (refreshTimeout) clearTimeout(refreshTimeout)
			refreshTimeout = setTimeout(unitConfig.getUnitConfigure, 60 * 1000)
		},
		() => {
			dispatch({ type: "IS_ERROR", payload: "Error occurred while configuring Ethernet settings" })
		}
	)

	const toggleModalHandler = () => dispatch({ type: "TOGGLE_MODAL" })

	const handleDhcpToggle = (checked) => dispatch({ type: "TOGGLE_DHCP", payload: checked })

	const handleInputChange = (name, value) => {
		const isValid = validateAddress(value)
		dispatch({ type: "SET_INVALID_INPUT", payload: { [name]: !isValid } })
		dispatch({ type: "UPDATE_UNIT_CONFIGURE", payload: { [name]: value } })
	}

	const handleOKButtonClicked = () => {
		dispatch({ type: "RESET_STATES", payload: data?.network?.Ethernet?.net_configuration.dhcp || false })
	}

	const confirmConfigureEthernetSettings = () => {
		const { address, netmask, gateway, dns } = state.requiredEthernetConfigureChange.net_configuration
		if (!state.isDhcpEnabled && (!address || !netmask)) {
			dispatch({
				type: "SET_INVALID_INPUT",
				payload: {
					address: !address,
					netmask: !netmask
				}
			})
			return
		}
		dispatch({ type: "IS_FETCHING", payload: true })
		dispatch({ type: "SET_DEVICE_ID", payload: deviceId })

		let postPayload

		const netConfig = {
			...state.requiredEthernetConfigureChange.net_configuration,
			gateway: gateway || "0.0.0.0",
			dns: dns || "0.0.0.0"
		}

		if (state.isDhcpEnabled) {
			postPayload = {
				...state.requiredEthernetConfigureChange,
				net_configuration: {
					dhcp: true
				}
			}
		} else {
			postPayload = {
				...state.requiredEthernetConfigureChange,
				net_configuration: {
					...netConfig,
					dhcp: false
				}
			}
		}
		postData(postPayload)
	}

	const cancelConfigureEthernetSettings = () => {
		dispatch({ type: "RESET_STATES", payload: data?.network?.Ethernet?.net_configuration.dhcp || false })
		dispatch({ type: "TOGGLE_MODAL", payload: false })
	}

	useEffect(() => {
		if (data) {
			dispatch({
				type: "TOGGLE_DHCP",
				payload: data?.network?.Ethernet?.net_configuration.dhcp || false
			})
			dispatch({
				type: "UPDATE_ORIGIN_ETHERNET_CONFIGURE",
				payload: data?.network?.Ethernet?.net_configuration
			})
			dispatch({ type: "SET_DEVICE_ID", payload: deviceId })
		}
	}, [data])

	useEffect(() => {
		if (deviceDetails.deviceDetailsData.state === "offline") {
			dispatch({ type: "SET_DISABLED", payload: true })
		}
	}, [deviceDetails.deviceDetailsData.state])

	useEffect(() => {
		if (state.originEthernetConfigure) {
			dispatch({
				type: "UPDATE_UNIT_CONFIGURE",
				payload: { ...state.originEthernetConfigure.netConfiguration }
			})
		}
	}, [state.originEthernetConfigure])

	return {
		state,
		postState,
		deviceState: deviceDetails.deviceDetailsData.state,
		validateAddress,
		handleDhcpToggle,
		handleInputChange,
		toggleModalHandler,
		handleOKButtonClicked,
		confirmConfigureEthernetSettings,
		cancelConfigureEthernetSettings
	}
}
